import type { ContactType } from "../types/contact";
import {
  type ContactTypeMeta,
  CONTACT_TYPE_ORDER,
  getContactTypeMetasForProfile,
} from "./profileRegistry";

type ProfileId = string | null | undefined;

export const ALL_CONTACT_TYPES: readonly ContactType[] = CONTACT_TYPE_ORDER;

export const isContactType = (value: string): value is ContactType =>
  (ALL_CONTACT_TYPES as readonly string[]).includes(value);

/**
 * Convert an unknown contact type string (e.g. from API/session data)
 * into a safe, supported `ContactType` for the UI.
 */
export const resolveContactType = (
  value: string | null | undefined,
  fallback: ContactType = "Intake",
): ContactType => {
  const v = String(value || "").trim();
  return v && isContactType(v) ? (v as ContactType) : fallback;
};

export const getContactTypeMetas = (profileId?: ProfileId): ContactTypeMeta[] =>
  getContactTypeMetasForProfile(profileId);

export const getContactTypeOptions = (
  profileId?: ProfileId
): { value: ContactType; label: string; icon: string }[] =>
  getContactTypeMetas(profileId).map((meta) => ({
    value: meta.value,
    label: meta.label,
    icon: meta.icon,
  }));

export const getContactTypeMeta = (
  value: ContactType,
  profileId?: ProfileId
): ContactTypeMeta =>
  getContactTypeMetas(profileId).find((m) => m.value === value) ?? {
    value,
    label: value,
    icon: "🔖",
    colorClass: "bg-gray-50 text-gray-700",
    requiredFields: 5,
  };


export const getContactTypeRequiredFields = (
  value: ContactType,
  profileId?: ProfileId
): number => getContactTypeMeta(value, profileId).requiredFields;
